import React, { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import http from "../../Api/http";
import StatusBadge from "./StatusBadge";
import { useAuth } from '../AuthContext';
import { useTranslation } from "react-i18next";
import { Truck, X, CheckCircle, Edit3, Trash2, Save, Minimize2, Maximize2, Barcode, Trash, Undo2, CheckCheck } from "lucide-react";
import Util from "../../Util";
import ConfirmDialog from "../confirm/ConfirmDialog";
import PERMISSIONS from "../../Permissions";
import ItemsSearch from "./ItemsSearch";
import Select from "react-select";
import { convertOffsetToTimes } from "framer-motion";

const Shipment = ({ open, onClose, shipmentId }) => {
    const id = shipmentId;
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar" || i18n.language === "he";

    const { user } = useAuth();
    const [shipment, setShipment] = useState({ Id: 0, Items: [], StatusId: 0, SupplierName: user?.Username });
    const [isLoading, setIsLoading] = useState(true);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [deletedItem, setDeletedItem] = useState(null);
    const [returnItem, setReturnItem] = useState(null);
    const [isReturnConfirmDialogOpen, setIsReturnConfirmDialogOpen] = useState(false);
    const [returnDoneDialogOpen, setIsReturnDoneDialogOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [buses, setBuses] = useState([]);
    const [users, setUsers] = useState([]);

    const [isItemsSearchOpen, setIsItemsSearchOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const [newItem, setNewItem] = useState({ Id: 0, Barcode: "", ItemName: "", Price: "", Qty: "", BusNo: '' });
    const [editingItem, setEditingItem] = useState(null);

    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [approveShipmentDialogOpen, setApproveShipmentDialogOpen] = useState(false);
    const [shipmentLog, setShipmentLog] = useState([]);
    const [shipmentLogDialogOpen, setOpenShipmentLogDialog] = useState(false);
    const [returnManagerDialogOpen, setReturnManagerDialogOpen] = useState(false);
    const [deliveryType, setDeliveryType] = useState("employee");
    const [deliveryEmployeeId, setDeliveryEmployeeId] = useState("");
    const [taxiName, setTaxiName] = useState("");
    const [taxiPhone, setTaxiPhone] = useState("");
    const [returnReason, setReturnReason] = useState("");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const txtBarcode = useRef();
    const txtName = useRef();
    const txtQuantity = useRef();
    const txtPrice = useRef();
    const txtDriverName = useRef();
    const txtDriverPhone = useRef();
    const txtReturnReason = useRef();
    const drpBus = useRef();
    const drpBusGrid = useRef();


    const isManager = user?.Role === 1;
    const isAssignedEmployee = (user?.Role === 1 || user?.Role === 4) && shipment.EmployeeId === user?.ID;
    const isSupplier = user?.Role === 3 && shipment?.SupplierName === user?.Username;
    const isOwner = shipment.UserId === user?.ID;

    // Load shipment
    const loadShipment = async () => {
        setIsLoading(true);
        if (!shipmentId) {
            setShipment({ Id: 0, Items: [], StatusId: 0, SupplierName: user?.Username });
            setIsLoading(false);
            return;
        }
        const res = await http.getJsonAxios(http.actions.Shipments.GetShipment, { OrderId: shipmentId }, t);
        if (res.status === 200 && res.data.success) {
            if (res.data.obj) {
                setShipment(res.data.obj);
            }
            else {
                setShipment({ Id: 0, Items: [], StatusId: 0, SupplierName: user?.Username });
                setIsLoading(false);
                return;
            }
        }
        else toast.error(res.message);
        setIsLoading(false);
    };

    // Load employees & users
    const loadEmployees = async () => {
        const res = await http.getJsonAxios(http.actions.Shipments.GetEmployees);
        if (res.status === 200 && res.data.success) {
            setEmployees(res.data.dataObject);
        }
    };
    const loadUsers = async () => {
        const res = await http.getJsonAxios(http.actions.Shipments.GetUsers);
        if (res.status === 200 && res.data.success) {
            setUsers(res.data.dataObject);
        };
    }
    const loadBuses = async () => {
        const res = await http.getJsonAxios(http.actions.Shipments.GetBusesList);
        if (res.status === 200 && res.data.success) {
            setBuses(res.data.dataObject);
        }
    };

    useEffect(() => {
        //window.scrollTo({ top: 0, behavior: "smooth" });
        if (!open) return;
        loadShipment();
        loadEmployees();
        loadUsers();
        loadBuses();
    }, [open]);

    if (!open) return null;

    const txtBarcode_KeyDown = async (e) => {
        if (e.key === 'Enter') {
            await getItemByBarcode(e.target.value);
        }
    }
    const getItemByBarcode = async (barcode) => {
        if (!barcode) {
            return;
        }
        try {
            setIsLoading(true);
            const res = await http.getJsonAxios(http.actions.Shipments.GetItemByBarcode, { Barcode: barcode }, t);
            if (res.status === 200 && res.data.success) {
                if (res.data.dataObject && res.data.dataObject.length > 0) {
                    const item = res.data.dataObject[0];
                    setNewItem({ Id: item.Id, Barcode: item.Barcode, ItemName: item.Name, Price: item.Price, Qty: 1 });
                    txtQuantity.current.focus();
                }
                else {
                    setNewItem({ Id: 0, Barcode: barcode, ItemName: '', Price: '', Qty: '', BusNo: '' });
                    txtName.current.focus();
                }
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    }
    const txtName_KeyDown = async (e) => {
        if (e.key === 'Enter') {
            txtQuantity.current.focus();
        }
    }
    const txtQuantity_KeyDown = async (e) => {
        if (e.key === 'Enter') {
            txtPrice.current.focus();
        }
    }
    const txtPrice_KeyDown = async (e) => {
        if (e.key === 'Enter') {
            drpBus.current.focus();
        }
    }
    const drpBus_KeyDown = async (e) => {
        if (e.key === 'Enter') {
            handleAddItem();
        }
    }

    const getShipmentLog = async () => {
        if (!shipmentId) {
            return;
        }
        try {
            setIsLoading(true);
            const res = await http.getJsonAxios(http.actions.Shipments.GetOrderLog, { OrderId: shipmentId }, t);
            if (res.status === 200 && res.data.success) {
                setShipmentLog(res.data.dataObject);
                setOpenShipmentLogDialog(true);
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    }

    const handleAddItem = async () => {
        if (!newItem.Barcode || !newItem.ItemName || !newItem.Price || !newItem.Qty) {
            txtBarcode.current.focus();
            toast.error(t('enterAllData'), Util.getErrorToast());
            return;
        }
        if (newItem.BusNo === -1 || newItem.BusNo === '') {
            drpBus.current.focus();
            toast.error(t('selectBusNo'), Util.getErrorToast());
            return;
        }
        try {
            setIsLoading(true);
            const obj = {
                Id: 0,
                OrderId: shipmentId,
                ItemId: newItem.Id,
                Barcode: newItem.Barcode,
                ItemName: newItem.ItemName,
                Qty: newItem.Qty,
                Price: newItem.Price,
                BusNo: newItem.BusNo
            }
            const res = await http.postJsonAxios(http.actions.Shipments.AddItem, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                shipment.Items.push(res.data.obj);
                loadShipment();
                setNewItem({ Barcode: "", ItemName: "", Price: "", Qty: "", BusNo: '' });
                txtBarcode.current.focus();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    const updateItem = async () => {
        if (!editingItem) return;
        try {
            const obj = {
                Id: editingItem.Id,
                OrderId: shipmentId,
                ItemName: editingItem.ItemName,
                Qty: editingItem.Qty,
                Price: editingItem.Price,
                BusNo: editingItem.BusNo
            }
            const res = await http.postJsonAxios(http.actions.Shipments.UpdateItem, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                setEditingItem(null);
                loadShipment();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    const onDeleteItem = async (item) => {
        if (!item) return;
        try {
            const obj = {
                OrderId: shipmentId,
                Id: item.Id,
                ItemName: item.ItemName,
                Price: item.Price,
                Qty: item.Qty
            };
            const res = await http.postJsonAxios(http.actions.Shipments.DeleteItem, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                setIsConfirmDialogOpen(false);
                loadShipment();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };
    const onReturnItem = async (item) => {
        if (!item) return;
        try {
            const obj = {
                OrderId: shipmentId,
                Id: item.Id,
                ItemName: item.ItemName,
                Price: item.Price,
                Qty: item.Qty
            };
            const res = await http.postJsonAxios(http.actions.Shipments.ReturnItem, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                setIsReturnConfirmDialogOpen(false);
                loadShipment();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };
    const onReturnDoneItem = async (item) => {
        if (!item) return;
        try {
            const obj = {
                OrderId: shipmentId,
                Id: item.Id,
                ItemName: item.ItemName,
                Price: item.Price,
                Qty: item.Qty
            };
            const res = await http.postJsonAxios(http.actions.Shipments.DoneReturnItem, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                setIsReturnDoneDialogOpen(false);
                loadShipment();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    const sendShipment = async () => {
        if (deliveryType === "employee" && !deliveryEmployeeId) {
            return toast.error(t('selectEmployee'), Util.getErrorToast());
        }
        if (deliveryType === "taxi_driver" && (!taxiName || !taxiPhone)) {
            if (!taxiName)
                txtDriverName.current.focus();
            else if (!taxiPhone)
                txtDriverPhone.current.focus();
            return toast.error(t('driverInfoError'), Util.getErrorToast());
        }
        let empName = "";
        if (deliveryType === "employee") {
            const emp = employees.find(e => e.ID === parseInt(deliveryEmployeeId));
            empName = emp ? emp.Name : "";
        }
        try {
            const obj = {
                OrderId: shipmentId,
                DeliveryType: deliveryType,
                DeliveryEmployeeId: deliveryType === "employee" ? deliveryEmployeeId : null,
                DriverName: deliveryType === "taxi_driver" ? taxiName : empName,
                DriverPhone: deliveryType === "taxi_driver" ? taxiPhone : null,
            }

            const res = await http.postJsonAxios(http.actions.Shipments.SendShipment, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                setSendDialogOpen(false);
                loadShipment();
            } else {
                toast.error(t('failed'));
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };

    const receiveShipment = async () => {
        try {
            setIsLoading(true);
            const res = await http.postJsonAxios(http.actions.Shipments.ReceiveShipment, { OrderId: shipmentId }, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                loadShipment();
                onClose();
            }
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    };

    const approveShipment = async () => {
        try {
            setIsLoading(true);
            const obj = {
                Id: shipmentId,
                StatusId: 6
            }
            const res = await http.postJsonAxios(http.actions.Shipments.ChangeShipmentStatus, obj, t);

            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                loadShipment();
                onClose();
            }
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    };
    const onApproveShipmentFromManager = async () => {
        try {
            setIsLoading(true);
            const obj = {
                Id: shipmentId,
                StatusId: 1
            }
            const res = await http.postJsonAxios(http.actions.Shipments.ChangeShipmentStatus, obj, t);

            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                loadShipment();
                onClose();
            }
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    };

    const markReturn = async () => {
        if (!shipmentId) return;
        if (!returnReason) {
            toast.error(t('returnReasonError'), Util.getErrorToast());
            txtReturnReason.current.focus();
            return
        }
        try {
            setIsLoading(true);
            const obj = {
                Id: shipmentId,
                StatusId: 4,
                Notes: returnReason
            }
            const res = await http.postJsonAxios(http.actions.Shipments.ChangeShipmentStatus, obj, t);

            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                loadShipment();
                onClose();
            }
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    }
    const confirmReturn = async () => {
        if (!shipmentId) return;
        try {
            setIsLoading(true);
            const obj = {
                Id: shipmentId,
                StatusId: 5,
            }
            const res = await http.postJsonAxios(http.actions.Shipments.ChangeShipmentStatus, obj, t);
            if (res.status === 200 && res.data.success) {
                toast.success(t('done'), Util.getSuccessToast());
                loadShipment();
                onClose();
            }
        }
        catch { }
        finally {
            setIsLoading(false);
        }
    };

    const busOptions = [
        { value: -1, label: t('selectBus') },
        { value: 0, label: t('shared') },
        ...buses.map(bus => ({
            value: bus.Id,
            label: bus.Id.toString()
        }))
    ];

    const total = shipment?.Items?.reduce((sum, i) => sum + i.Price * i.Qty, 0) || 0;
    const netTotal = shipment?.Items?.filter(i => i.StatusId !== 4 && i.StatusId !== 5).reduce((sum, i) => sum + i.Price * i.Qty, 0) || 0;


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center px-4" dir={t('dir')}>
            <div className={`bg-white rounded-2xl w-full max-w-7xl max-h-[95vh] min-h-[70vh] overflow-auto p-6 relative
            ${isFullscreen
                    ? "fixed inset-0 w-screen h-screen max-w-none rounded-none"
                    : "max-w-full sm:max-w-7xl rounded-3xl"
                }`}
            >
                {/* Fullscreen Toggle */}
                <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className={isRTL ? "absolute top-2 left-10 text-xl" : "absolute top-2 right-10 text-xl"}
                >
                    {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                </button>
                <button className={isRTL ? "absolute top-2 left-2 text-xl" : "absolute top-2 right-2 text-xl"} onClick={onClose}>✖️</button>
                <br />
                <>
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-6">

                        {/* Left Side */}
                        <div>
                            <h2 className="text-2xl font-bold mb-1 flex flex-wrap items-center gap-2 cursor-pointer" onClick={() => getShipmentLog()}>
                                <span>{t('shipment')} #{shipment.Id}</span>
                                <StatusBadge status={shipment.StatusId} />
                            </h2>

                            <div className="text-sm text-gray-500 mt-1">
                                {t('theSupplier')}: {shipment.SupplierName}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-3 w-full lg:w-auto">

                            {Util.hasPermission(user, PERMISSIONS.Approve_And_Send_ToSupplier) && shipment.StatusId === 0 && (
                                <button
                                    onClick={() => setApproveShipmentDialogOpen(true)}
                                    className="bg-amber-500 text-white px-4 py-2 rounded flex items-center gap-2
                                               hover:bg-amber-700 shadow-lg shadow-amber-600/25"
                                >
                                    <Truck size={16} /> {t('approveShipment')}
                                </button>
                            )}

                            {isSupplier && shipment.StatusId === 1 && (
                                <button
                                    onClick={() => setSendDialogOpen(true)}
                                    className="bg-amber-500 text-white px-4 py-2 rounded flex items-center gap-2
                                               hover:bg-amber-700 shadow-lg shadow-amber-600/25"
                                >
                                    <Truck size={16} /> {t('sendShipment')}
                                </button>
                            )}

                            {isAssignedEmployee && shipment.StatusId === 2 && (
                                <button
                                    onClick={() => receiveShipment(shipment.Id)}
                                    className="bg-emerald-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                               hover:bg-emerald-700 shadow-lg shadow-emerald-600/25"
                                >
                                    <CheckCircle className="w-4 h-4" /> {t('confirmReceived')}
                                </button>
                            )}
                            {
                                isManager && [2].includes(shipment.StatusId) && Util.hasPermission(user, PERMISSIONS.Receiving_Orders_WithDriver) &&
                                shipment.SentWithType === 2 && (
                                    <button
                                        onClick={() => receiveShipment(shipment.Id)}
                                        className="bg-emerald-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                                   hover:bg-emerald-700 shadow-lg shadow-emerald-600/25"
                                    >
                                        <CheckCircle className="w-4 h-4" /> {t('confirmReceived')}
                                    </button>
                                )
                            }
                            {
                                isManager && [3].includes(shipment.StatusId) && (
                                    <>
                                        {
                                            Util.hasPermission(user, PERMISSIONS.Return_Order) && (
                                                <button
                                                    onClick={() => setReturnManagerDialogOpen(true)}
                                                    className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                                               hover:bg-red-700 shadow-lg shadow-red-600/25"
                                                >
                                                    <CheckCircle className="w-4 h-4" /> {t('returnToSupplier')}
                                                </button>
                                            )
                                        }
                                        {
                                            Util.hasPermission(user, PERMISSIONS.Return_Order) && (
                                                <button
                                                    onClick={() => approveShipment()}
                                                    className="bg-emerald-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                                    hover:bg-emerald-700 shadow-lg shadow-emerald-600/25"
                                                >
                                                    <CheckCircle className="w-4 h-4" /> {t('approved')}
                                                </button>
                                            )
                                        }
                                    </>
                                )
                            }

                            {isSupplier && shipment.StatusId === 4 && (
                                <button
                                    onClick={() => confirmReturn()}
                                    className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                        hover:bg-purple-700 shadow-lg shadow-purple-600/25"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    {t('confirmReturnReceived')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* DATA INFO */}
                    <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-6">
                        <div className="space-y-8">
                            {/* Add Item */}
                            {(isSupplier || isOwner) && shipment.StatusId === 0 && (
                                <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                                    <h3 className="font-bold mb-2">{t('addItem')}</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                                        <div className="w-full relative">
                                            <input
                                                autoFocus
                                                ref={txtBarcode}
                                                placeholder={t('barcode')}
                                                value={newItem.Barcode}
                                                onChange={e =>
                                                    setNewItem({ ...newItem, Barcode: e.target.value })
                                                }
                                                onBlur={e => getItemByBarcode(e.target.value)}
                                                onKeyDown={txtBarcode_KeyDown}
                                                className="w-full border py-1 px-2 pr-10 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                            />

                                            {/* 🔍 زر البحث داخل الحقل */}
                                            <button
                                                type="button"
                                                onClick={() => setIsItemsSearchOpen(true)}
                                                className={isRTL ? "absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-600" :
                                                    "absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-600"
                                                }
                                                tabIndex={-1}
                                            >
                                                🔍
                                            </button>
                                        </div>
                                        <div>
                                            {/* <label className="block mb-1 text-gray-700 font-medium">{t('name')}</label> */}
                                            <input ref={txtName} placeholder={t('name')} value={newItem.ItemName}
                                                onChange={e => setNewItem({ ...newItem, ItemName: e.target.value })} onKeyDown={txtName_KeyDown}
                                                className="w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                                        </div>
                                        <div>
                                            {/* <label className="block mb-1 text-gray-700 font-medium">{t('quantity')}</label> */}
                                            <input ref={txtQuantity} placeholder={t('quantity')} type="number" value={newItem.Qty}
                                                onChange={e => setNewItem({ ...newItem, Qty: e.target.value })} onKeyDown={txtQuantity_KeyDown}
                                                className="w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                                        </div>
                                        <div>
                                            {/* <label className="block mb-1 text-gray-700 font-medium">{t('price')}</label> */}
                                            <input ref={txtPrice} placeholder={t('price')} type="number" value={newItem.Price}
                                                onChange={e => setNewItem({ ...newItem, Price: e.target.value })} onKeyDown={txtPrice_KeyDown}
                                                className="w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
                                        </div>
                                        <div>
                                            {/* Bus */}
                                            {/* <label className="block mb-1 text-gray-700 font-medium">{t('bus')}</label> */}
                                            {/* <select ref={drpBus} value={newItem.BusNo} onChange={e => setNewItem({ ...newItem, BusNo: e.target.value })}
                                                onKeyDown={drpBus_KeyDown}
                                                className="border px-2 rounded w-full border focus:ring-2 focus:ring-blue-500 outline-none" dir={t('dir')}>
                                                <option key={-1} value={-1}>{t('selectBus')}</option>
                                                <option key={0} value={0}>{t('shared')}</option>
                                                {buses.map(bus => (
                                                    <option key={bus.Id} value={bus.Id}>
                                                        {bus.Id}
                                                    </option>
                                                ))}
                                            </select> */}
                                            <Select
                                                ref={drpBus}
                                                value={busOptions.find(o => o.value === newItem.BusNo)}
                                                onChange={(selected) => {
                                                    setNewItem({
                                                        ...newItem,
                                                        BusNo: selected.value
                                                    })
                                                }
                                                }
                                                options={busOptions}
                                                isSearchable={true}
                                                placeholder={t('selectBus')}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: 33,   // 👈 يقلل الارتفاع
                                                        height: 33,
                                                        fontSize: 13
                                                    }),

                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        height: 32,
                                                        padding: "0 8px"
                                                    }),

                                                    input: (base) => ({
                                                        ...base,
                                                        margin: 0,
                                                        padding: 0
                                                    }),

                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: 32
                                                    })
                                                }}
                                            />
                                        </div>
                                        <button onClick={handleAddItem} className="bg-blue-600 text-white px-4 py-2 rounded">{t('add')}</button>
                                    </div>
                                </div>
                            )}

                            {/* Items Table */}
                            <div className="border rounded-lg overflow-hidden mb-4">

                                {/* ===================== */}
                                {/* 🖥 DESKTOP TABLE */}
                                {/* ===================== */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="p-3">{t('barcode')}</th>
                                                <th>{t('name')}</th>
                                                <th>{t('quantity')}</th>
                                                <th>{t('price')}</th>
                                                <th>{t('busNo')}</th>
                                                <th>{t('status')}</th>
                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {!shipment.Items || shipment.Items.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="text-center p-4 text-gray-400">
                                                        {t('noItems')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                shipment.Items.map(item => (
                                                    <tr key={item.Id} className="border-t text-center">

                                                        <td className="p-3">{item.Barcode}</td>

                                                        <td className="break-words whitespace-normal">
                                                            {item.ItemName}
                                                        </td>

                                                        <td>{item.Qty}</td>

                                                        {/* PRICE */}
                                                        <td>
                                                            {editingItem?.Id === item.Id ? (
                                                                <input
                                                                    type="number"
                                                                    value={editingItem?.Price}
                                                                    onChange={e =>
                                                                        setEditingItem({
                                                                            ...editingItem,
                                                                            Price: e.target.value
                                                                        })
                                                                    }
                                                                    className="w-20 border py-1 px-2 rounded"
                                                                />
                                                            ) : item.Price}
                                                        </td>

                                                        {/* BUS */}
                                                        <td>
                                                            {editingItem?.Id === item.Id ? (
                                                                <Select
                                                                    ref={drpBusGrid}
                                                                    value={busOptions.find(o => o.value === editingItem?.BusNo)}
                                                                    onChange={(selected) =>
                                                                        setEditingItem({
                                                                            ...editingItem,
                                                                            BusNo: selected.value
                                                                        })
                                                                    }
                                                                    options={busOptions}
                                                                    isSearchable={true}
                                                                    placeholder={t('selectBus')}
                                                                    menuPosition="fixed"
                                                                    menuShouldScrollIntoView={false}
                                                                    styles={{
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            minHeight: 30,   // 👈 يقلل الارتفاع
                                                                            height: 30,
                                                                            fontSize: 13
                                                                        }),

                                                                        valueContainer: (base) => ({
                                                                            ...base,
                                                                            height: 32,
                                                                            padding: "0 8px"
                                                                        }),

                                                                        input: (base) => ({
                                                                            ...base,
                                                                            margin: 0,
                                                                            padding: 0
                                                                        }),

                                                                        indicatorsContainer: (base) => ({
                                                                            ...base,
                                                                            height: 32
                                                                        })
                                                                    }}
                                                                />
                                                            ) : (
                                                                item.BusNo === 0 ? t('shared') : item.BusNo
                                                            )}
                                                        </td>

                                                        {/* STATUS */}
                                                        <td>
                                                            <StatusBadge status={item.StatusId} />
                                                        </td>

                                                        {/* ACTIONS (UNCHANGED) */}
                                                        <td className="flex gap-2 justify-center">

                                                            {editingItem?.Id === item.Id ? (
                                                                <button
                                                                    onClick={updateItem}
                                                                    className="h-8 w-8 text-emerald-600"
                                                                >
                                                                    <Save size={16} />
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    {(isSupplier || isOwner) &&
                                                                        shipment.StatusId === 0 && (
                                                                            <button
                                                                                className="h-8 w-8 text-blue-600"
                                                                                onClick={() =>
                                                                                    setEditingItem(item)
                                                                                }
                                                                            >
                                                                                <Edit3 size={16} />
                                                                            </button>
                                                                        )}

                                                                    {(isSupplier || isOwner) &&
                                                                        shipment.StatusId === 0 && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setDeletedItem(item);
                                                                                    setIsConfirmDialogOpen(true);
                                                                                }}
                                                                                className="h-8 w-8 text-red-600"
                                                                            >
                                                                                <Trash2 size={16} />
                                                                            </button>
                                                                        )}

                                                                    {Util.hasPermission(user, PERMISSIONS.Return_Item) &&
                                                                        shipment.StatusId === 3 &&
                                                                        item.StatusId === 1 && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setReturnItem(item);
                                                                                    setIsReturnConfirmDialogOpen(true);
                                                                                }}
                                                                                className="h-8 w-8 text-red-600"
                                                                            >
                                                                                <Undo2 size={16} />
                                                                            </button>
                                                                        )}

                                                                    {isSupplier &&
                                                                        shipment.StatusId === 3 &&
                                                                        item.StatusId === 4 && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setReturnItem(item);
                                                                                    setIsReturnDoneDialogOpen(true);
                                                                                }}
                                                                                className="h-8 w-8 text-red-600"
                                                                            >
                                                                                <CheckCheck size={16} />
                                                                            </button>
                                                                        )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ===================== */}
                                {/* 📱 MOBILE CARDS */}
                                {/* ===================== */}
                                <div className="md:hidden space-y-3 p-3">

                                    {!shipment.Items || shipment.Items.length === 0 ? (
                                        <div className="text-center text-gray-400">
                                            {t('noItems')}
                                        </div>
                                    ) : (
                                        shipment.Items.map(item => (
                                            <div
                                                key={item.Id}
                                                className="border rounded-lg p-3 bg-white shadow-sm"
                                            >

                                                {/* HEADER */}
                                                <div className="flex justify-between items-center mb-2">
                                                    <div className="font-bold">{item.ItemName}</div>
                                                    <StatusBadge status={item.StatusId} />
                                                </div>

                                                {/* INFO */}
                                                <div className="text-sm space-y-1">

                                                    <div>
                                                        <b>{t('barcode')}:</b> {item.Barcode}
                                                    </div>

                                                    <div>
                                                        <b>{t('quantity')}:</b> {item.Qty}
                                                    </div>

                                                    <div>
                                                        <b>{t('price')}:</b>{" "}
                                                        {editingItem?.Id === item.Id ? (
                                                            <input
                                                                type="number"
                                                                value={editingItem?.Price}
                                                                onChange={e =>
                                                                    setEditingItem({
                                                                        ...editingItem,
                                                                        Price: e.target.value
                                                                    })
                                                                }
                                                                className="w-24 border px-2 py-1 rounded"
                                                            />
                                                        ) : (
                                                            item.Price
                                                        )}
                                                    </div>

                                                    <div>
                                                        <b>{t('busNo')}:</b>{" "}
                                                        {editingItem?.Id === item.Id ? (
                                                            <Select
                                                                ref={drpBusGrid}
                                                                value={busOptions.find(o => o.value === editingItem?.BusNo)}
                                                                onChange={(selected) =>
                                                                    setEditingItem({
                                                                        ...editingItem,
                                                                        BusNo: selected.value
                                                                    })
                                                                }
                                                                options={busOptions}
                                                                isSearchable={true}
                                                                placeholder={t('selectBus')}
                                                                menuPosition="fixed"
                                                                menuShouldScrollIntoView={false}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: 30,   // 👈 يقلل الارتفاع
                                                                        height: 30,
                                                                        fontSize: 13
                                                                    }),

                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        height: 32,
                                                                        padding: "0 8px"
                                                                    }),

                                                                    input: (base) => ({
                                                                        ...base,
                                                                        margin: 0,
                                                                        padding: 0
                                                                    }),

                                                                    indicatorsContainer: (base) => ({
                                                                        ...base,
                                                                        height: 32
                                                                    })
                                                                }}
                                                            />
                                                        ) : (
                                                            item.BusNo === 0 ? t('shared') : item.BusNo
                                                        )}
                                                    </div>
                                                </div>

                                                {/* ACTIONS (ALL PRESERVED) */}
                                                <div className="flex justify-end gap-2 mt-3">

                                                    {editingItem?.Id === item.Id ? (
                                                        <button
                                                            onClick={updateItem}
                                                            className="text-emerald-600"
                                                        >
                                                            <Save size={16} />
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {(isSupplier || isOwner) &&
                                                                shipment.StatusId === 1 && (
                                                                    <button
                                                                        onClick={() => setEditingItem(item)}
                                                                        className="text-blue-600"
                                                                    >
                                                                        <Edit3 size={16} />
                                                                    </button>
                                                                )}

                                                            {(isSupplier || isOwner) &&
                                                                shipment.StatusId === 1 && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setDeletedItem(item);
                                                                            setIsConfirmDialogOpen(true);
                                                                        }}
                                                                        className="text-red-600"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                )}

                                                            {Util.hasPermission(user, PERMISSIONS.Return_Item) &&
                                                                shipment.StatusId === 3 &&
                                                                item.StatusId === 1 && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setReturnItem(item);
                                                                            setIsReturnConfirmDialogOpen(true);
                                                                        }}
                                                                        className="text-red-600"
                                                                    >
                                                                        <Undo2 size={16} />
                                                                    </button>
                                                                )}

                                                            {isSupplier &&
                                                                shipment.StatusId === 3 &&
                                                                item.StatusId === 4 && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setReturnItem(item);
                                                                            setIsReturnDoneDialogOpen(true);
                                                                        }}
                                                                        className="text-red-600"
                                                                    >
                                                                        <CheckCheck size={16} />
                                                                    </button>
                                                                )}
                                                        </>
                                                    )}

                                                </div>

                                            </div>
                                        ))
                                    )}

                                </div>

                            </div>

                        </div>

                        <div className="space-y-4">
                            {/* Delivery Info */}
                            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                                <h3 className="font-bold mb-2">{t('deliveryInfo')}</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {
                                        shipment.StatusId === 1 ? (
                                            <div className="text-center text-muted-foreground py-4 text-sm">
                                                {t('notSent')}
                                            </div>
                                        ) :
                                            <div>
                                                <p className="text-xs text-muted-foreground mb-1">{t('deliveryType')}</p>
                                                {
                                                    shipment.DeliveryType === "employee" ?
                                                        (
                                                            <div>
                                                                <p className=" text-blue-700 bg-blue-50 inline-block px-2 py-0.5 rounded">
                                                                    {t('employee')}: {shipment.EmployeeName}
                                                                </p>
                                                            </div>
                                                        )
                                                        :
                                                        shipment.DeliveryType === "taxi_driver" ? (
                                                            <div>
                                                                <p className="text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded mb-1">
                                                                    {t('driverName')}: {shipment.DriverName}
                                                                </p>
                                                                <p className="text-amber-700 bg-amber-50 inline-block px-2 py-0.5 rounded mb-1">
                                                                    {shipment.DriverPhone}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p>zead</p>
                                                        )
                                                }
                                            </div>
                                    }
                                </div>
                                {shipment.ReturnReason && (
                                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-xs text-red-600 font-bold mb-1">
                                            {t('returnReason')}:
                                        </p>
                                        <p className="text-sm text-red-900">
                                            {shipment.ReturnReason}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Financial Summary */}
                            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                                <h3 className="font-bold mb-2">{t('financialSummary')}</h3>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-muted-foreground text-sm">
                                        {t('totalItems')}
                                    </span>
                                    <span className="font-bold text-blue-800">
                                        {shipment.Items ? shipment.Items.length : 0}
                                        {/* {shipment.Items.reduce((acc, item) => acc + item.Qty, 0)} */}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-lg mt-4 pt-4 border-t border-border/50">
                                    <span className="font-bold">{t('total')}:</span>
                                    <span className="font-bold text-blue-800">
                                        {Util.formatNumber(total.toFixed(2))}{" "}
                                        {t('currency')}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-lg mt-4 pt-4 border-t border-border/50">
                                    <span className="font-bold">{t('netAmount')}:</span>
                                    <span className="font-bold text-green-800">
                                        {Util.formatNumber(netTotal.toFixed(2))}{" "}
                                        {t('currency')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>

                {/* Send Dialog */}
                {sendDialogOpen && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center px-4" dir={t('dir')}>
                        <div className="bg-white p-6 rounded-2xl max-w-md w-full relative" dir={t('dir')}>
                            <button className={isRTL ? "absolute top-4 left-4 text-xl" : "absolute top-4 right-4 text-xl"} onClick={() => setSendDialogOpen(false)}>✖️</button>
                            <h3 className="text-lg font-bold mb-4">{t('sendShipment')}</h3>

                            <div className="space-y-2">
                                <label className="block mb-1 text-gray-700 font-medium">{t('sendType')}</label>
                                <select value={deliveryType} onChange={e => setDeliveryType(e.target.value)}
                                    className="border px-2 py-1 rounded w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="employee">{t('abdoEmployee')}</option>
                                    <option value="taxi_driver">{t('taxiDriver')}</option>
                                </select>

                                {deliveryType === "employee" ? (
                                    <select value={deliveryEmployeeId} onChange={e => {
                                        setDeliveryEmployeeId(e.target.value);
                                    }}
                                        className="border px-2 py-1 rounded w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none">
                                        <option value="">{t('selectEmployee')}</option>
                                        {employees.map(emp => (
                                            <option key={emp.ID} value={emp.ID}>
                                                {emp.Name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <input
                                            ref={txtDriverName}
                                            placeholder={t('driverName')}
                                            value={taxiName}
                                            onChange={e => setTaxiName(e.target.value)}
                                            className="border px-2 py-1 rounded w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        />

                                        <input
                                            ref={txtDriverPhone}
                                            placeholder={t('driverPhone')}
                                            value={taxiPhone}
                                            onChange={e => setTaxiPhone(e.target.value)}
                                            className="border px-2 py-1 rounded w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                )}
                            </div>

                            <button onClick={sendShipment} className="bg-green-600 text-white px-6 py-2 rounded mt-4 flex items-center gap-2"><Truck size={16} /> {t('send')}</button>
                        </div>
                    </div>
                )}

                {/* Return Manager Dialog */}
                {returnManagerDialogOpen && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center px-4 sm:px-6" dir={t('dir')}>

                        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl w-full max-w-md relative flex flex-col gap-4">

                            {/* Close Button */}
                            <button
                                className={isRTL ? "absolute top-4 left-4 text-xl" : "absolute top-4 right-4 text-xl"}
                                onClick={() => setReturnManagerDialogOpen(false)}
                            >
                                ✖️
                            </button>

                            {/* Title */}
                            <h3 className="text-lg font-bold">{t('returnToSupplierTitle')}</h3>

                            {/* Input */}
                            <div className="flex flex-col gap-2 w-full">
                                <label className="text-sm font-medium">{t('returnReason')}</label>
                                <input
                                    ref={txtReturnReason}
                                    value={returnReason}
                                    onChange={(e) => setReturnReason(e.target.value)}
                                    placeholder={t('returnReasonEx')}
                                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                />
                            </div>

                            {/* Action Button */}
                            <button
                                className="w-full bg-red-600 text-white px-4 py-2 rounded-md flex justify-center items-center gap-2
                                    hover:bg-red-700 shadow-lg shadow-red-600/25 transition"
                                onClick={() => markReturn()}
                            >
                                {t('returnToSupplier')}
                            </button>
                        </div>
                    </div>
                )}

                {/* shipmentLogDialogOpen */}
                {
                    shipmentLogDialogOpen &&
                    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center px-4" dir={t('dir')}>
                        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col relative">
                            {/* Close */}
                            <button
                                className={isRTL ? "absolute top-4 left-4 text-xl" : "absolute top-4 right-4 text-xl"}
                                onClick={() => setOpenShipmentLogDialog(false)}
                            >
                                ✖️
                            </button>

                            <h3 className="text-lg font-bold mb-4">{t('shipmentLog')} #{shipmentId}</h3>

                            {/* ================= DESKTOP TABLE ================= */}
                            <div className="hidden md:flex flex-col flex-1 border rounded-lg overflow-hidden">

                                {/* Scroll Vertical Only */}
                                <div className="overflow-y-auto flex-1">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                                            <tr>
                                                <th className="p-3">##</th>
                                                <th className="p-3">{t('createDate')}</th>
                                                <th className="p-3">{t('user')}</th>
                                                <th className="p-3">{t('note')}</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {!shipmentLog || shipmentLog.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="text-center p-6 text-gray-400">
                                                        {t('noData')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                shipmentLog.map((item, index) => (
                                                    <tr key={index} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="p-3">{index + 1}</td>
                                                        <td className="p-3 whitespace-nowrap">
                                                            {Util.formatShortDate(item.InsDate)}
                                                        </td>
                                                        <td className="p-3">{item.EmployeeName}</td>
                                                        <td className="p-3">{item.Notes}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ================= MOBILE CARDS ================= */}
                            <div className="md:hidden flex-1 overflow-y-auto space-y-3">
                                {!shipmentLog || shipmentLog.length === 0 ? (
                                    <div className="text-center p-6 text-gray-400">
                                        {t('noData')}
                                    </div>
                                ) : (
                                    shipmentLog.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 shadow space-y-2"
                                        >
                                            <div className="flex justify-between">
                                                <span className="font-bold">#{index + 1}</span>
                                                <span className="text-sm text-gray-500">
                                                    {Util.formatShortDate(item.InsDate)}
                                                </span>
                                            </div>

                                            <div>
                                                <span className="text-xs text-gray-400">{t('user')}</span>
                                                <div>{item.EmployeeName}</div>
                                            </div>

                                            <div>
                                                <span className="text-xs text-gray-400">{t('note')}</span>
                                                <div className="text-sm">{item.Notes}</div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                        </div>
                    </div>
                }

                {/* Confirm Dialog */}
                {
                    isConfirmDialogOpen &&
                    <ConfirmDialog
                        isOpen={isConfirmDialogOpen}
                        title={t('deleteItem')}
                        message={t('deleteItemConfirm')}
                        onConfirm={() => onDeleteItem(deletedItem)}
                        onCancel={() => {
                            setDeletedItem(null);
                            setIsConfirmDialogOpen(false)
                        }}
                    />
                }
                {/* Confirm Dialog */}
                {
                    isReturnConfirmDialogOpen &&
                    <ConfirmDialog
                        isOpen={isReturnConfirmDialogOpen}
                        title={t('returnItem')}
                        message={t('returnItemConfirm')}
                        onConfirm={() => onReturnItem(returnItem)}
                        onCancel={() => {
                            setReturnItem(null);
                            setIsReturnConfirmDialogOpen(false)
                        }}
                    />
                }
                {/* Confirm Dialog */}
                {
                    returnDoneDialogOpen &&
                    <ConfirmDialog
                        isOpen={returnDoneDialogOpen}
                        title={t('returnItem')}
                        message={t('returnItemDoneConfirm')}
                        onConfirm={() => onReturnDoneItem(returnItem)}
                        onCancel={() => {
                            setReturnItem(null);
                            setIsReturnDoneDialogOpen(false)
                        }}
                    />
                }

                {/* Confirm Dialog */}
                {
                    approveShipmentDialogOpen &&
                    <ConfirmDialog
                        isOpen={approveShipmentDialogOpen}
                        title={t('approveShipment')}
                        message={t('approveShipmentConfirm')}
                        onConfirm={() => onApproveShipmentFromManager(deletedItem)}
                        onCancel={() => {
                            setApproveShipmentDialogOpen(false)
                        }}
                    />
                }
                <ItemsSearch
                    isOpen={isItemsSearchOpen}
                    onClose={() => setIsItemsSearchOpen(false)}
                    onSelect={(item) => {
                        setNewItem({ Id: item.Id, Barcode: item.Barcode, ItemName: item.Name, Price: item.Price, Qty: 1, BusNo: '' });
                        txtQuantity.current.focus();
                    }}
                />
            </div>
        </div >
    );
};

export default Shipment;